import { takeEvery, put, all } from 'redux-saga/effects';

import * as types from '../../actions/vacancies/types';
import * as actions from '../../actions/vacancies';
import * as authUserActions from '../../actions/auth-user';

import httpStatusCodes from '../../constants/http-status-codes';
import applicationFlowTypes from '../../constants/application-flow-types';

import history from '../../tools/history';
import apiFetcher from '../../tools/api-fetcher';
import handleError from '../../tools/error-helper';

function* getPublicVacancyPreescreeningQuestions({ schoolId, vacancyId }) {
  yield put(actions.setPublicPrescreeningQuestionsLoading(true));

  try {
    const { data } = yield apiFetcher.get(
      `/public/schools/${schoolId}/vacancies/${vacancyId}/questions/pre-screening`
    );

    yield put(actions.setPublicVacancyPrescreeningQuestions(data));
  } catch (exception) {
    handleError(exception);
  }

  yield put(actions.setPublicPrescreeningQuestionsLoading(false));
}

function* getPublicVacancyApplication({ schoolId, vacancyId }) {
  yield put(actions.setPublicVacancyApplicationLoading(true));

  try {
    const { data: applicationId } = yield apiFetcher
      .auth()
      .get(
        `/public/schools/${schoolId}/vacancy/${vacancyId}/applications/from-user`
      );

    yield put(actions.setPublicVacancyApplication(applicationId));
  } catch (exception) {
    switch (exception.response && exception.response.status) {
      case httpStatusCodes.NotFound:
        yield put(actions.setPublicVacancyApplication());
        break;
      default:
        handleError(exception);
        break;
    }
  }

  yield put(actions.setPublicVacancyApplicationLoading(false));
}

function* createPublicVacancyApplication({
  schoolId,
  vacancyId,
  applicationFlowType,
  data,
  vacancyDiscoverySourceId
}) {
  yield put(actions.setPublicVacancyApplicationLoading(true));

  const applicationRequestData = {
    vacancyDiscoverySourceId
  };

  try {
    switch (applicationFlowType) {
      case applicationFlowTypes.fromLoggedUser:
        const { data: fromUserApplicationId } = yield apiFetcher
          .auth()
          .post(
            `/public/schools/${schoolId}/vacancy/${vacancyId}/applications/from-user`,
            applicationRequestData
          );

        yield put(
          actions.setPublicVacancyApplication(
            fromUserApplicationId,
            applicationFlowTypes.fromLoggedUser
          )
        );
        yield history.push(
          `/candidate/my-applications/${fromUserApplicationId}`
        );
        break;

      case applicationFlowTypes.fromNewUser:
        const {
          data: fromNewUserApplicationId
        } = yield apiFetcher.post(
          `/public/schools/${schoolId}/vacancy/${vacancyId}/applications/from-new-user`,
          {
            ...data,
            ...applicationRequestData
          }
        );

        yield put(
          actions.setPublicVacancyApplication(
            fromNewUserApplicationId,
            applicationFlowTypes.fromNewUser
          )
        );
        break;

      case applicationFlowTypes.fromApliUser:
        let authSuccess = true;

        yield put(authUserActions.setLoading(true));

        try {
          const { data: authData } = yield apiFetcher.post('auth-tokens', {
            email: data.email,
            password: data.password
          });

          yield put(authUserActions.setUserFromAuthData(authData));
        } catch (exception) {
          authSuccess = false;
          if (!exception.response) {
            handleError(exception);
          } else {
            let errorMessage = exception.response.statusText;

            if (exception.response.status === httpStatusCodes.Unauthorized) {
              errorMessage = errorMessage.substring(2);
            }

            yield put(actions.setApplicationErrors([errorMessage]));
          }
        }

        yield put(authUserActions.setLoading(false));

        if (authSuccess) {
          const {
            data: fromAppliUserApplicationId
          } = yield apiFetcher
            .auth()
            .post(
              `/public/schools/${schoolId}/vacancy/${vacancyId}/applications/from-user`,
              applicationRequestData
            );

          yield put(
            actions.setPublicVacancyApplication(
              fromAppliUserApplicationId,
              applicationFlowTypes.fromApliUser
            )
          );
          yield history.push(
            `/candidate/my-applications/${fromAppliUserApplicationId}`
          );
        }
        break;
      case applicationFlowTypes.fromSearchAssociatesUser:
        yield put(authUserActions.setLoading(true));
        try {
          const {
            data: fromSearchAssociatesUserData
          } = yield apiFetcher.post(
            `/public/schools/${schoolId}/vacancy/${vacancyId}/applications/from-search-associates-user`,
            {
              ...applicationRequestData,
              ...data
            }
          );

          const {
            vacancyApplicationId,
            ...authData
          } = fromSearchAssociatesUserData;

          yield put(authUserActions.setUserFromAuthData(authData));

          yield history.push(
            `/candidate/my-applications/${vacancyApplicationId}`
          );
        } finally {
          yield put(authUserActions.setLoading(false));
        }

        break;

      default:
        break;
    }
  } catch (exception) {
    switch (exception.response && exception.response.status) {
      case httpStatusCodes.Unauthorized:
        if (
          applicationFlowType === applicationFlowTypes.fromSearchAssociatesUser
        ) {
          yield put(actions.setApplicationStatus(exception.response.status));
        } else {
          yield put(
            actions.setApplicationErrors([
              exception.response.statusText.substring(2)
            ])
          );
        }
        break;
      case httpStatusCodes.Conflict:
        if (applicationFlowType === applicationFlowTypes.fromNewUser) {
          yield put(
            actions.setApplicationErrors({
              email: exception.response.statusText
            })
          );
        } else if (
          applicationFlowType === applicationFlowTypes.fromSearchAssociatesUser
        ) {
          const { headers } = exception.response;

          yield put(
            actions.setPublicVacancyApplication(
              headers['apli-applicationid'],
              applicationFlowTypes.fromSearchAssociatesUser
            )
          );
        }
        break;
      case httpStatusCodes.NotPermissions:
        if (
          applicationFlowType === applicationFlowTypes.fromSearchAssociatesUser
        ) {
          yield put(actions.setApplicationStatus(exception.response.status));
        }
        break;
      default:
        handleError(exception);
        break;
    }
  }

  yield put(actions.setPublicVacancyApplicationLoading(false));
}

export default function* schoolPublicVacancySaga() {
  yield all([
    takeEvery(
      types.VACANCY_GET_PUBLIC_PRESCREENING_QUESTIONS,
      getPublicVacancyPreescreeningQuestions
    ),
    takeEvery(
      types.VACANCY_GET_PUBLIC_APPLICATION,
      getPublicVacancyApplication
    ),
    takeEvery(
      types.VACANCY_CREATE_PUBLIC_APPLICATION,
      createPublicVacancyApplication
    )
  ]);
}



// WEBPACK FOOTER //
// ./src/sagas/school/school-public-vacancy.js